import API from './API';

// -----------------------------------------------------------------------------
//  Letture paginate da @nestjsx/crud.
//
//  ⚠️ `page` NON È FACOLTATIVO. Il backend risponde in DUE forme diverse:
//
//     CON `page`  ->  { data: [...], total: N }   ← il totale vero del server
//     SENZA       ->  [ ... ]                     ← un array nudo
//
//  Senza `page` non arriva `total`, quindi è impossibile accorgersi che il
//  backend ha TRONCATO a `MAX_CRUD_LIMIT`: le righe oltre il tetto spariscono
//  in silenzio e l'elenco sembra semplicemente più corto. Sul gestionale admin
//  è stato misurato: 3 prenotazioni da 986,18 € forzando il limite a 1 riga
//  davano 97,60 € senza alcun avviso.
//
//  Oggi nel gestionale partner NESSUNA chiamata passa `page`. Questo modulo
//  esiste perché non si debba più ricordarselo: ogni lettura di elenco passa
//  di qui e riceve `truncated`, che la pagina deve mostrare invece di far
//  credere che quelle siano tutte le righe.
//
//  ⚠️ Migrazione: la forma di ritorno è { rows, total, truncated }, NON un
//  array. Convertire una chiamata significa toccare anche chi la consuma
//  (oggi molti fanno `response.data.filter(...)`). Si converte pagina per
//  pagina, insieme al suo rifacimento — non tutto in una volta.
// -----------------------------------------------------------------------------

// Tetto del backend (MAX_CRUD_LIMIT): oltre questo le righe verrebbero
// troncate senza dirlo.
export const MAX_ROWS = 2000;

/** Normalizza le due forme di risposta in { rows, total }. */
export function normalize(response) {
  const body = response?.data;
  if (Array.isArray(body)) return { rows: body, total: body.length };
  return { rows: body?.data ?? [], total: body?.total ?? 0 };
}

/**
 * GET paginata. Ritorna { rows, total, truncated }.
 * `truncated` è vero quando il server dichiara più righe di quante ne ha
 * mandate: in quel caso l'elenco è parziale e va detto a schermo.
 */
export async function fetchPaged(path, { limit = MAX_ROWS, ...params } = {}) {
  const res = await API.get(path, { params: { ...params, page: 1, limit } });
  const { rows, total } = normalize(res);
  return { rows, total, truncated: total > rows.length };
}

/**
 * Estremo superiore ESCLUSIVO per le finestre di data.
 *
 * `data` è un datetime, quindi `data <= '2026-09-08'` significa
 * `<= 2026-09-08 00:00:00`: tutto quello che succede nel giorno finale resta
 * fuori. Si usa `data < dayAfter(fine)`.
 */
export function dayAfter(isoDate) {
  // ⚠️ La `Z` e i metodi UTC non sono un dettaglio. Costruendo la data in ora
  // LOCALE, `new Date('2026-09-08T00:00:00')` a Roma vale 2026-09-07T22:00Z:
  // aggiungendo un giorno e ritagliando la parte data si riottiene
  // '2026-09-08', cioè lo stesso giorno di partenza.
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

export default { fetchPaged, normalize, dayAfter, MAX_ROWS };
